import axios from 'axios';
import { shouldCacheRoute, cacheResponse, getCachedResponse } from './offlineCache';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global processing state callbacks (set by ProcessingContext)
let onRequestStart = null;
let onRequestEnd = null;

// Function to register processing callbacks
export const registerProcessingCallbacks = (onStart, onEnd) => {
  onRequestStart = onStart;
  onRequestEnd = onEnd;
};

// ============================================
// COLD-START RETRY CONFIG
// ============================================
// After deployment, the backend takes ~30-40s to initialize.
// Instead of showing "Login failed", we auto-retry with backoff.
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 2000,        // 2s, 4s, 8s backoff
  retryableStatuses: [502, 503, 504, 0],  // 0 = network error (no response)
};

function isRetryableError(error) {
  // Network error (backend not up yet)
  if (!error.response) return true;
  // Gateway/proxy errors during cold start
  if (RETRY_CONFIG.retryableStatuses.includes(error.response.status)) return true;
  return false;
}

function getRetryDelay(attempt) {
  return RETRY_CONFIG.baseDelay * Math.pow(2, attempt); // exponential backoff
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,  // 30s timeout (generous for cold-start)
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store',
    'Pragma': 'no-cache',
  },
});

// Track active requests
let activeRequests = 0;

// Add token to requests and trigger processing state
api.interceptors.request.use((config) => {
  // Determine correct token based on the API path being called
  // Staff endpoints (/staff/*, /support/*, /admin-tickets/*) use staffToken
  // All other endpoints (merchant) use regular token
  const requestPath = config.url || '';
  const isStaffEndpoint = requestPath.includes('/staff/') || 
                          requestPath.includes('/support/') ||
                          requestPath.includes('/admin-tickets/') ||
                          requestPath.includes('/crm/');
  
  const staffToken = localStorage.getItem('staffToken');
  const token = localStorage.getItem('token');
  
  if (isStaffEndpoint && staffToken) {
    config.headers.Authorization = `Bearer ${staffToken}`;
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (staffToken) {
    // Fallback to staff token if no merchant token exists
    config.headers.Authorization = `Bearer ${staffToken}`;
  }
  
  // Trigger processing start
  activeRequests++;
  if (onRequestStart && activeRequests === 1) {
    onRequestStart();
  }
  
  return config;
});

// Handle responses and stop processing
api.interceptors.response.use(
  (response) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (onRequestEnd && activeRequests === 0) {
      onRequestEnd();
    }
    // Cache successful GET responses for offline access
    if (response.config.method === 'get' && shouldCacheRoute(response.config.url)) {
      cacheResponse(response.config.url, response.data);
    }
    return response;
  },
  async (error) => {
    const config = error.config;

    // If offline and this is a GET to a cacheable route, serve from cache
    if (!navigator.onLine && config && config.method === 'get' && shouldCacheRoute(config.url)) {
      const cached = getCachedResponse(config.url);
      if (cached) {
        activeRequests = Math.max(0, activeRequests - 1);
        if (onRequestEnd && activeRequests === 0) onRequestEnd();
        return { data: cached.data, status: 200, config, headers: {}, _fromCache: true, _stale: cached.stale };
      }
    }
    
    // Auto-retry for cold-start / deployment errors
    if (isRetryableError(error) && config && !config.__retryCount) {
      config.__retryCount = 0;
    }
    
    if (isRetryableError(error) && config && config.__retryCount < RETRY_CONFIG.maxRetries) {
      config.__retryCount += 1;
      const delay = getRetryDelay(config.__retryCount - 1);
      
      console.log(`[API] Server not ready, retrying (${config.__retryCount}/${RETRY_CONFIG.maxRetries}) in ${delay}ms...`);
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return api(config);
    }

    activeRequests = Math.max(0, activeRequests - 1);
    if (onRequestEnd && activeRequests === 0) {
      onRequestEnd();
    }
    
    // Enhance error message for cold-start failures (all retries exhausted)
    if (isRetryableError(error)) {
      const coldStartMsg = 'Server is starting up after deployment. Please wait 30 seconds and try again.';
      if (error.response) {
        error.response.data = { detail: coldStartMsg };
      } else {
        error.message = coldStartMsg;
      }
    }
    
    // Handle auth failures: both 401 (token expired) AND 403 "Not authenticated" (no token sent)
    const httpStatus = error.response?.status;
    // Coerce detail to a string. The backend sometimes returns `detail` as
    // an object (e.g. nested BlueDart error: {message, request_id, status_code})
    // which would crash `.toLowerCase()` and mask the real error in the toast.
    const rawDetail = error.response?.data?.detail;
    const errorDetail = (
      typeof rawDetail === 'string'
        ? rawDetail
        : (rawDetail?.message || JSON.stringify(rawDetail || '') || '')
    ).toLowerCase();
    
    const isAuthFailure = 
      (httpStatus === 401 && (errorDetail.includes('expired') || errorDetail.includes('invalid token') || errorDetail.includes('not authenticated'))) ||
      (httpStatus === 403 && errorDetail.includes('not authenticated'));
    
    if (isAuthFailure && config && !config.__refreshAttempted) {
      // Try silent refresh BEFORE forcing logout
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        config.__refreshAttempted = true;
        try {
          console.log('[AUTH] Access token expired, attempting silent refresh...');
          const refreshRes = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          if (refreshRes.ok) {
            const data = await refreshRes.json();
            localStorage.setItem('token', data.access_token);
            if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
            console.log('[AUTH] Silent refresh successful — retrying original request');
            // Retry the original request with new token
            config.headers.Authorization = `Bearer ${data.access_token}`;
            return api(config);
          }
        } catch (refreshErr) {
          console.log('[AUTH] Silent refresh failed:', refreshErr.message);
        }
      }

      // Refresh failed or no refresh token — force logout
      if (!window.__authRedirecting) {
        window.__authRedirecting = true;
        console.log(`[AUTH] Session invalid (${httpStatus}), redirecting to login`);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('staffToken');
        setTimeout(() => {
          window.__authRedirecting = false;
          window.location.href = '/login';
        }, 300);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Get the backend URL for API calls
 * Falls back to current origin if REACT_APP_BACKEND_URL is not set
 */
export const getBackendUrl = () => {
  // Check for environment variable
  if (BACKEND_URL) {
    return BACKEND_URL;
  }
  
  // Fallback: use same origin (works for Emergent deployments where frontend and backend are on same domain)
  return window.location.origin;
};

/**
 * Convert document URL to proper backend API URL
 * Handles both old format (/uploads/kyc/...), new format (/api/kyc/view/...), 
 * and Cloudinary URLs (https://res.cloudinary.com/...)
 */
export const getDocumentUrl = (url) => {
  if (!url) return null;
  
  const backendUrl = getBackendUrl();
  
  // Cloudinary URLs must go through backend proxy (bypasses delivery blocks)
  if (url.includes('cloudinary.com') || url.includes('res.cloudinary')) {
    return `${backendUrl}/api/kyc/proxy-view?url=${encodeURIComponent(url)}`;
  }
  
  // If it's already a proper /api/kyc/ URL, prepend backend URL
  if (url.startsWith('/api/kyc/')) {
    return `${backendUrl}${url}`;
  }
  
  // If it's already a full URL with http/https
  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (url.includes('/api/kyc/')) {
      return url;
    }
    // Extract path and reconstruct
    const filename = url.split('/').pop();
    return `${backendUrl}/api/kyc/view/${filename}`;
  }
  
  // Handle /uploads/kyc/merchant_id/filename or /uploads/kyc/filename format
  if (url.includes('/uploads/kyc/')) {
    const pathAfterKyc = url.split('/uploads/kyc/').pop();
    return `${backendUrl}/api/kyc/view/${pathAfterKyc}`;
  }
  
  // Handle relative paths
  if (url.startsWith('/')) {
    const filename = url.split('/').pop();
    return `${backendUrl}/api/kyc/view/${filename}`;
  }
  
  // Plain filename
  return `${backendUrl}/api/kyc/view/${url}`;
};

export default api;